import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Shield, LogOut, Calculator } from "lucide-react";
import { useAuth, isAdminRole } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { ThemeToggle } from "../ThemeToggle";
import { logoFor } from "../../lib/brand";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        [
          "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors",
          isActive
            ? "bg-secondary text-secondary-foreground font-medium"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")
      }
    >
      <Icon size={16} strokeWidth={1.7} />
      <span>{label}</span>
    </NavLink>
  );
}

export function TopBar() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border/60"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <NavLink to="/" className="flex items-center shrink-0" data-testid="brand">
          <img
            src={logoFor(theme)}
            alt="SK Kapoor — GPT Hub"
            className="h-10 md:h-12 w-auto object-contain select-none"
            draggable={false}
          />
        </NavLink>

        {/* Center nav (desktop) */}
        <nav className="hidden md:flex items-center gap-1">
          <NavItem to="/" icon={LayoutGrid} label="Dashboard" testId="nav-dashboard" />
          {isAdminRole(user?.role) && (
            <NavItem to="/admin" icon={Shield} label="Admin" testId="nav-admin" />
          )}
        </nav>

        {/* Right user block */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-border/60">
            <div className="text-right leading-tight">
              <div className="text-sm font-medium truncate max-w-[140px]" data-testid="topbar-user-name">
                {user?.name || "User"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {user?.role === "master_admin" ? "master admin" : user?.role}
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-secondary border border-border/70 flex items-center justify-center text-sm font-semibold">
              {initial}
            </div>
          </div>
          <button
            data-testid="topbar-logout"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            aria-label="Logout"
            className="h-10 w-10 flex items-center justify-center rounded-full border border-border/60 hover:bg-secondary transition-colors"
          >
            <LogOut size={16} strokeWidth={1.7} />
          </button>
        </div>
      </div>
    </header>
  );
}
